import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSiteSettingsService } from '../../../services/adminSiteSettingsService';
import { Save, Plus, Trash2, Shield } from 'lucide-react';
import { Button, Input, TextArea } from '../../shared';
import { useToastContext } from '../../../contexts/ToastContext';

const ICON_OPTIONS = ['Users', 'Lock', 'Eye', 'Phone', 'Shield', 'CheckCircle', 'AlertTriangle', 'FileText'];

const EMPTY_SAFETY = {
  hero: { title: '', subtitle: '' },
  guidelines: [],
  verification: [],
  reporting:  [],
  resources: [],
  emergency: { title: '', description: '', phone: '', reportButtonText: '' },
};

const SafetySettings = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [formData, setFormData] = useState(EMPTY_SAFETY);

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['adminSiteSettings'],
    queryFn: () => adminSiteSettingsService.get(),
  });

  useEffect(() => {
    const ss = settingsResponse?.data?.safety_settings;
    if (ss && typeof ss === 'object') {
      setFormData({
        hero: { title: ss.hero?.title ?? '', subtitle: ss.hero?.subtitle ?? '' },
        guidelines: Array.isArray(ss.guidelines) ? ss.guidelines : [],
        verification: Array.isArray(ss.verification) ? ss.verification : [],
        reporting: Array.isArray(ss.reporting) ? ss.reporting : [],
        resources: Array.isArray(ss.resources) ? ss.resources : [],
        emergency: {
          title: ss.emergency?.title ?? '',
          description: ss.emergency?.description ?? '',
          phone: ss.emergency?.phone ?? '',
          reportButtonText: ss.emergency?.reportButtonText ?? '',
        },
      });
    }
  }, [settingsResponse]);

  const saveMutation = useMutation({
    mutationFn: (data) => adminSiteSettingsService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSiteSettings']);
      queryClient.invalidateQueries(['siteSettings']);
      toast.success(null, 'Safety settings saved successfully');
    },
    onError: (error) => {
      toast.error(null, error.response?.data?.message || 'Failed to save');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullSettings = { ...settingsResponse?.data, safety_settings: formData };
    saveMutation.mutate(fullSettings);
  };

  const updateHero = (field, value) => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, [field]: value } }));
  const updateEmergency = (field, value) => setFormData((prev) => ({ ...prev, emergency: { ...prev.emergency, [field]: value } }));

  const updateGuideline = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.guidelines || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, guidelines: arr };
    });
  };
  const addGuideline = () => setFormData((prev) => ({ ...prev, guidelines: [...(prev.guidelines || []), { icon: 'Shield', title: '', description: '' }] }));
  const removeGuideline = (i) => setFormData((prev) => ({ ...prev, guidelines: prev.guidelines.filter((_, idx) => idx !== i) }));

  const updateVerification = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.verification || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, verification: arr };
    });
  };
  const addVerification = () => setFormData((prev) => ({ ...prev, verification: [...(prev.verification || []), { title: '', description: '' }] }));
  const removeVerification = (i) => setFormData((prev) => ({ ...prev, verification: prev.verification.filter((_, idx) => idx !== i) }));

  const updateReporting = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.reporting || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, reporting: arr };
    });
  };
  const addReporting = () => setFormData((prev) => ({ ...prev, reporting: [...(prev.reporting || []), { title: '', description: '' }] }));
  const removeReporting = (i) => setFormData((prev) => ({ ...prev, reporting: prev.reporting.filter((_, idx) => idx !== i) }));

  const updateResource = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.resources || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, resources: arr };
    });
  };
  const addResource = () => setFormData((prev) => ({ ...prev, resources: [...(prev.resources || []), { title: '', description: '', action: '' }] }));
  const removeResource = (i) => setFormData((prev) => ({ ...prev, resources: prev.resources.filter((_, idx) => idx !== i) }));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow">
      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Safety & Security Page
          </h2>
          <p className="text-sm text-gray-600">Configure the content for the public Safety page.</p>
        </div>

        {/* Hero */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Hero Section</h3>
          <div className="space-y-4">
            <Input label="Title" value={formData.hero?.title || ''} onChange={(e) => updateHero('title', e.target.value)} placeholder="Safety & Security" />
            <TextArea label="Subtitle" value={formData.hero?.subtitle || ''} onChange={(e) => updateHero('subtitle', e.target.value)} rows={2} placeholder="Your safety is our top priority..." />
          </div>
        </div>

        {/* Guidelines */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Safety Guidelines</h3>
            <Button type="button" variant="outline" size="sm" onClick={addGuideline}><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-4">
            {(formData.guidelines || []).map((item, i) => (
              <div key={i} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Guideline {i + 1}</span>
                  <button type="button" onClick={() => removeGuideline(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                    <select value={item.icon || 'Shield'} onChange={(e) => updateGuideline(i, 'icon', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                      {ICON_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <Input label="Title" value={item.title || ''} onChange={(e) => updateGuideline(i, 'title', e.target.value)} />
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <TextArea value={item.description || ''} onChange={(e) => updateGuideline(i, 'description', e.target.value)} rows={2} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Verification */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Vendor Verification</h3>
            <Button type="button" variant="outline" size="sm" onClick={addVerification}><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-4">
            {(formData.verification || []).map((item, i) => (
              <div key={i} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Item {i + 1}</span>
                  <button type="button" onClick={() => removeVerification(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
                <Input label="Title" value={item.title || ''} onChange={(e) => updateVerification(i, 'title', e.target.value)} />
                <TextArea label="Description" value={item.description || ''} onChange={(e) => updateVerification(i, 'description', e.target.value)} rows={2} />
              </div>
            ))}
          </div>
        </div>

        {/* Reporting */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Reporting Issues</h3>
            <Button type="button" variant="outline" size="sm" onClick={addReporting}><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-4">
            {(formData.reporting || []).map((item, i) => (
              <div key={i} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Item {i + 1}</span>
                  <button type="button" onClick={() => removeReporting(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
                <Input label="Title" value={item.title || ''} onChange={(e) => updateReporting(i, 'title', e.target.value)} />
                <TextArea label="Description" value={item.description || ''} onChange={(e) => updateReporting(i, 'description', e.target.value)} rows={2} />
              </div>
            ))}
          </div>
        </div>

        {/* Resources */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Resources</h3>
            <Button type="button" variant="outline" size="sm" onClick={addResource}><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-4">
            {(formData.resources || []).map((item, i) => (
              <div key={i} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Resource {i + 1}</span>
                  <button type="button" onClick={() => removeResource(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="Title" value={item.title || ''} onChange={(e) => updateResource(i, 'title', e.target.value)} />
                  <Input label="Button Text" value={item.action || ''} onChange={(e) => updateResource(i, 'action', e.target.value)} placeholder="Download Guide" />
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <TextArea value={item.description || ''} onChange={(e) => updateResource(i, 'description', e.target.value)} rows={2} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency */}
        <div className="border border-red-200 rounded-lg p-6 bg-red-50/30">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Support Section</h3>
          <div className="space-y-4">
            <Input label="Title" value={formData.emergency?.title || ''} onChange={(e) => updateEmergency('title', e.target.value)} placeholder="Emergency Support" />
            <TextArea label="Description" value={formData.emergency?.description || ''} onChange={(e) => updateEmergency('description', e.target.value)} rows={2} />
            <Input label="Phone Number" value={formData.emergency?.phone || ''} onChange={(e) => updateEmergency('phone', e.target.value)} placeholder="1-800-VENDA-911" />
            <Input label="Report Button Text" value={formData.emergency?.reportButtonText || ''} onChange={(e) => updateEmergency('reportButtonText', e.target.value)} placeholder="Report Safety Issue" />
          </div>
        </div>

        <Button type="submit" disabled={saveMutation.isPending}>
          <Save className="w-4 h-4 mr-2" />
          Save Safety Settings
        </Button>
      </form>
    </div>
  );
};

export default SafetySettings;
